import { Card } from "@/ingot";
import { type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { CartItem, Catalog } from "@/api/types";
import { VariantRow } from "@/components/cart/VariantRow";
import MeshThumbnail from "@/components/MeshThumbnail";

import { useDisplayMoney } from "@/lib/currency";
import { buildPartMeta } from "@/lib/partMeta";
import { cx } from "@/lib/utils";
import { getOperationTone, operationToneColor } from "@/lib/operationTone";
import { resolveColorOptionHex } from "@/lib/meshSnapshot";
import { DynamicCartLineSummary } from "@/processes/dynamic/DynamicCartLineSummary";
import { NO_ROTATION, rotationOfInputs } from "@/processes/dynamic/interactiveInputs";
import { partPreviewType } from "@/processes/partPreviewType";
import type { FrontendModule } from "@/processes/types";

/**
 * One ``OrderItem`` (= one uploaded DXF/STL) inside an operation
 * section. Renders a thumbnail, file name + variant count, a table of
 * ``VariantRow``s with a checkbox column, the inline finish dropdown,
 * and an "Add variant" footer button.
 *
 * Variant grouping is implicit: each ``CartLine`` is one variant of
 * the same DXF/STL upload. PR2 keeps the simple flat list; richer
 * "duplicate this variant" workflow lands in PR3 with the bulk bar.
 */
export function PartCard({
  item,
  catalog,
  locale,
  moduleMap,
  onDeleteItem,
  onDeleteLine,
  removeItemLabel,
  removeLineLabel,
  editLineLabel,
  maxExtraVariantsPerItem,
  previewHref,
  nameHref,
  addConfigHref,
  processSlug,
  leadingControl,
  disableFinish = false,
  selectableVariants,
  onUpdateLine,
  processLabel,
  currencyCode,
  netPricesLabelled = false,
}: {
  item: CartItem;
  catalog: Catalog | undefined;
  locale: string;
  moduleMap: Map<string, FrontendModule>;
  onDeleteItem: () => void;
  onDeleteLine: (lineId: number) => void;
  removeItemLabel: string;
  removeLineLabel: string;
  editLineLabel: string;
  maxExtraVariantsPerItem: number | null;
  previewHref?: string;
  nameHref?: string;
  addConfigHref: string;
  processSlug: string;
  /**
   * Optional control rendered before the preview thumbnail — used by
   * MyProjectDetail to add an item-level checkbox for the "add
   * selected to cart" affordance. Cart route leaves this undefined.
   */
  leadingControl?: ReactNode;
  /**
   * When true, this surface is treated as non-cart (MyProjectDetail) —
   * per-variant cart-selection checkboxes default off. Kept as the
   * default source for ``selectableVariants``.
   */
  disableFinish?: boolean;
  /**
   * Override the default per-variant checkbox visibility. Defaults to
   * ``!disableFinish`` (cart route shows checkboxes; MyProjectDetail
   * may opt back in for per-variant add-to-cart selection).
   */
  selectableVariants?: boolean;
  /**
   * PR20 — when provided, inline line edits route through this
   * callback instead of cart's ``useUpdateCartLine`` hook. Used by
   * MyProjectDetail to dispatch to ``useUpdateProjectLine``.
   */
  onUpdateLine?: (
    lineId: number,
    payload: import("@/processes/types").CartLineUpdatePayload,
  ) => void;
  /** KAN-1027 — popisek libovolného procesu pro rozpad ceny u varianty.
   *  Karta zná jen svou primárku; navazující operace patří jinému
   *  procesu a jméno pro ni musí přijít od volajícího. */
  processLabel?: (processType: string) => string;
  /** Účetní měna košíku — předává se dál do rozpadu ceny u varianty. */
  currencyCode?: string | null;
  /** KAN-2189 — částky dílu a variant jsou bez DPH; u plátce to musí říct,
   *  protože hlavička operace nad nimi může ukazovat cenu s DPH. */
  netPricesLabelled?: boolean;
}): JSX.Element {
  const { t } = useTranslation("cart");
  const { format, currency } = useDisplayMoney(locale);
  const operations = item.operations ?? [];
  const primaryOp = operations.find((op) => op.order_index === 0);
  const processType = primaryOp?.process_type ?? processSlug;
  const module = moduleMap.get(processType);
  // Schema-driven summary for every process; the generic renderer also
  // covers modules missing from the map (metadata still loading).
  const CartLineSummaryComponent =
    module?.CartLineSummary ?? DynamicCartLineSummary;
  const columnHeaders =
    module && module.cartColumnHeaders.length > 0
      ? module.cartColumnHeaders
      : ["col.material", "col.quantity"];
  const toneColor = operationToneColor(module);
  const tone = getOperationTone(toneColor);

  const primarySnapshot =
    primaryOp?.config_snapshot ?? item.lines[0]?.config_snapshot ?? null;
  // KAN-1703 — uložená orientace z interaktivního vstupu; díl bez něj se
  // kreslí neotočený (KAN-1706).
  const meshRotation = rotationOfInputs(primaryOp?.interactive_inputs) ?? NO_ROTATION;
  const meshColor = resolveColorOptionHex(catalog, primarySnapshot);

  // KAN-928 — the plan limit is „extra variants per part"; the customer
  // thinks in variants, so show ``used / cap`` with the first one counted.
  // A cap of ONE variant is not information (nothing to add, nothing to
  // count): no badge, no link — the footer simply isn't there.
  const variantCap =
    maxExtraVariantsPerItem === null ? null : maxExtraVariantsPerItem + 1;
  const variantsUsed = Math.max(1, item.lines.length);
  const variantLimitReached = variantCap !== null && variantsUsed >= variantCap;
  const variantsCounter =
    variantCap !== null && variantCap > 1
      ? t("variantsCounter", { used: variantsUsed, cap: variantCap })
      : null;

  const partTotal = item.lines.reduce(
    (sum, line) => sum + (line.line_total_czk ?? 0),
    0,
  );
  const partQty = item.lines.reduce((sum, line) => sum + line.quantity, 0);
  const warningsCount = item.warnings_count ?? 0;
  const metaLine = buildPartMeta(item.analysis, t);
  // KAN-1027 — hlavička dílu neříkala, že díl někam POKRAČUJE: zákazník
  // si přidal povrchovou úpravu, zaplatil za ni polovinu ceny a v
  // záhlaví položky po ní nezbyla stopa. Primárku (index 0) sem
  // nepíšeme — tu už nese sekce, ve které karta stojí.
  const chainSteps = operations
    .filter((op) => op.order_index !== 0)
    .sort((a, b) => a.order_index - b.order_index)
    .map((op) => processLabel?.(op.process_type) ?? op.process_type);

  // G5 — route the cart thumbnail by the part's ``preview_type`` (KAN-1401:
  // one operation may take 2D and 3D files), falling back to the process's
  // declared capability (surfaced via ProcessOut). No per-process branch; a
  // truly unknown module falls back to "none" → the placeholder slot below.
  const previewType = partPreviewType(item.preview_type, module?.preview_type);

  const preview = (
    <div
      className="block h-16 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-surface-2 transition-colors hover:border-ink-3"
      data-testid={`cart-item-preview-${item.id}`}
    >
      {previewType === "svg" && item.preview_url ? (
        <img
          src={item.preview_url}
          alt={item.file_name}
          className="h-full w-full object-contain"
        />
      ) : previewType === "mesh" ? (
        <MeshThumbnail
          src={item.mesh_preview_url}
          rotation={meshRotation}
          fileName={item.file_name}
          color={meshColor}
          height={64}
          className="h-full w-full"
          fallback={
            <span className="mono rounded border border-border bg-surface-2 px-2 py-1 text-[10px] uppercase tracking-widest text-ink-3">
              {item.file_name.split(".").pop()?.toUpperCase() ?? "3D"}
            </span>
          }
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-[10px] text-ink-4">
          {t("partCard.noPreview")}
        </div>
      )}
    </div>
  );

  return (
    <Card
      padded={false}
      className="overflow-hidden"
      data-testid={`cart-item-${item.id}`}
    >
      <header className="flex items-start gap-3 bg-surface-2 px-4 py-3">
        {leadingControl}
        {previewHref ? (
          <Link
            to={previewHref}
            aria-label={t("openDetail", { defaultValue: "Otevřít detail dílu" })}
          >
            {preview}
          </Link>
        ) : (
          preview
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {nameHref ? (
              <Link
                to={nameHref}
                className="mono text-sm font-semibold text-ink hover:underline"
                data-testid={`cart-item-name-${item.id}`}
              >
                {item.file_name}
              </Link>
            ) : (
              <span
                className="mono text-sm font-semibold text-ink"
                data-testid={`cart-item-name-${item.id}`}
              >
                {item.file_name}
              </span>
            )}
            <span
              className="inline-flex h-5 items-center gap-1 rounded-md border px-2 text-[11px] font-medium"
              style={{
                background: tone.bg,
                color: tone.ink,
                borderColor: tone.border,
              }}
              data-testid={`cart-item-${item.id}-variant-count`}
            >
              {t("partCard.variantCount", {
                defaultValue: "{{count}} variant",
                count: item.lines.length,
              })}
            </span>
            <span className="mono inline-flex h-5 items-center rounded-md border border-border bg-surface px-2 text-[11px] font-medium text-ink-2">
              {t("partCard.qtyPill", {
                defaultValue: "{{count}} ks",
                count: partQty,
              })}
            </span>
            {chainSteps.length > 0 && (
              <span
                className="inline-flex h-5 items-center rounded-md border border-border bg-surface px-2 text-[11px] font-medium text-ink-2"
                data-testid={`cart-item-${item.id}-chain`}
              >
                {t("partCard.chainSteps", {
                  defaultValue: "→ {{steps}}",
                  steps: chainSteps.join(" → "),
                })}
              </span>
            )}
            {warningsCount > 0 && (
              <span
                className="inline-flex h-5 items-center gap-1 rounded-md border border-warn-border bg-warn-bg px-2 text-[11px] font-medium text-warn"
                title={t("operationSection.warningsPillTitle", {
                  defaultValue: "Některé díly potřebují pozornost",
                })}
              >
                <span aria-hidden="true">⚠</span>
                {t("partCard.warningsShort", {
                  defaultValue: "{{count}} upoz.",
                  count: warningsCount,
                })}
              </span>
            )}
          </div>
          {metaLine && (
            <p
              className="mono mt-1 text-[10.5px] text-ink-3"
              data-testid={`cart-item-${item.id}-meta`}
            >
              {metaLine}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <span
            className="mono text-[15px] font-semibold tabular-nums text-ink"
            data-testid={`cart-item-${item.id}-total`}
          >
            {format(partTotal)}
          </span>
          <span className="mono text-[10px] uppercase tracking-[0.08em] text-ink-4">
            {netPricesLabelled
              ? t("partCard.subtotalCaptionNet")
              : t("partCard.subtotalCaption", { defaultValue: "Σ za díl" })}
          </span>
          <button
            type="button"
            onClick={onDeleteItem}
            className="mt-1 text-[11px] text-ink-3 transition-colors hover:text-danger"
            data-testid={`cart-item-${item.id}-remove`}
          >
            {removeItemLabel}
          </button>
        </div>
      </header>

      {catalog ? (
        // KAN-1877 — `relative` je nosná: sr-only hlavičky (Výběr/Odebrat) jsou
        // `position: absolute`, a bez pozicovaného předka je jejich containing
        // block kořen dokumentu. overflow-x-auto je pak neořízne a na telefonu
        // roztáhnou stránku (375 → 616 px).
        <div className="relative overflow-x-auto border-t border-border">
          {/* KAN-2121 — na telefonu varianta jako karta (``table-stack``):
              cena/ks a celkem bez vodorovného posouvání. */}
          <table className="table-stack w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="text-left">
                <Th srOnly>{t("partCard.selectColumnHeader", { defaultValue: "Výběr" })}</Th>
                {columnHeaders.map((key) => (
                  <Th key={key}>{t(key)}</Th>
                ))}
                <Th align="right">
                  {netPricesLabelled
                    ? t("col.unitPriceNet", { currency })
                    : t("col.unitPrice", { defaultValue: "{{currency}}/ks", currency })}
                </Th>
                <Th align="right">
                  {netPricesLabelled
                    ? t("col.lineTotalNet")
                    : t("col.lineTotal", { defaultValue: "Celkem" })}
                </Th>
                <Th align="right" srOnly>{removeLineLabel}</Th>
              </tr>
            </thead>
            <tbody>
              {item.lines.map((line, idx) => (
                <VariantRow
                  key={line.id}
                  line={line}
                  catalog={catalog}
                  locale={locale}
                  configSnapshot={
                    line.config_snapshot ?? primaryOp?.config_snapshot ?? null
                  }
                  CartLineSummary={CartLineSummaryComponent}
                  editHref={`${addConfigHref}?edit=${line.id}`}
                  editLabel={editLineLabel}
                  onRemove={() => onDeleteLine(line.id)}
                  removeLabel={removeLineLabel}
                  processSlug={processType}
                  toneColor={toneColor}
                  variantIndex={idx + 1}
                  item={item}
                  selectable={selectableVariants ?? !disableFinish}
                  onUpdateLine={onUpdateLine}
                  processLabel={processLabel}
                  currencyCode={currencyCode}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="px-4 py-3 text-sm text-ink-3">{t("loadingCatalog")}</p>
      )}

      {variantLimitReached && variantCap === 1 ? null : variantLimitReached ? (
        <div className="border-t border-dashed border-border px-4 py-2 text-center">
          <span
            className="text-[12px] text-ink-3"
            title={t("variantLimitReachedTitle", {
              count: maxExtraVariantsPerItem ?? 0,
            })}
            data-testid={`cart-item-add-config-${item.id}-disabled`}
          >
            {variantsCounter}
            <span className="ml-2 text-ink-4">{t("variantLimitReachedHint")}</span>
          </span>
        </div>
      ) : (
        <Link
          to={addConfigHref}
          className={cx(
            "group block border-t border-dashed border-border px-4 py-2.5 text-center transition-colors",
            // PR14 — hover background driven by operation tone. The tone
            // is a runtime colour (admin-authored category hex), so it
            // rides in as a CSS variable and the class stays literal for
            // Tailwind's build-time scan.
            "hover:bg-[var(--op-hover-bg)]",
          )}
          style={{ "--op-hover-bg": tone.bg } as CSSProperties}
          data-testid={`cart-item-add-config-${item.id}`}
          title={t("partCard.addVariantHint", {
            defaultValue: "stejný soubor, jiný materiál / tloušťka",
          })}
        >
          <span className="mono text-[11px] uppercase tracking-[0.08em] text-ink-3 transition-colors group-hover:text-ink">
            + {t("addConfig", { defaultValue: "Přidat variantu" })}
          </span>
          {variantsCounter && (
            <span
              className="ml-2 text-[10.5px] text-ink-4"
              data-testid={`cart-item-variants-counter-${item.id}`}
            >
              {variantsCounter}
            </span>
          )}
          <span className="ml-2 text-[10.5px] text-ink-4">
            {t("partCard.addVariantHint", {
              defaultValue: "stejný soubor, jiný materiál / tloušťka",
            })}
          </span>
        </Link>
      )}
    </Card>
  );
}

function Th({
  children,
  align = "left",
  srOnly = false,
}: {
  children: ReactNode;
  align?: "left" | "right";
  srOnly?: boolean;
}): JSX.Element {
  return (
    <th
      scope="col"
      className={`mono border-b border-border px-3 py-2 text-[10.5px] uppercase tracking-[0.08em] text-ink-3 ${
        align === "right" ? "text-right" : "text-left"
      } ${srOnly ? "sr-only" : ""}`}
    >
      {children}
    </th>
  );
}
